/* QUANTUM STORE — THEME JS / QTM.SYS V1.0 */

'use strict';

const _i18n = JSON.parse(document.getElementById('qs-i18n')?.textContent || '{}');

/* ── BARCODE STRIP ─────────────────────────────────────────────────────── */
class BarcodeStrip extends HTMLElement {
  connectedCallback() {
    const seed   = this.dataset.seed || 'QTM.SYS';
    const color  = this.style.color  || '#0A0A0A';
    const height = parseInt(this.style.height) || 18;
    const widths = this._generate(seed);
    this.style.height = height + 'px';
    this.innerHTML = widths.map((w, i) =>
      `<span style="width:${w}px;height:100%;display:block;background:${i % 2 === 0 ? color : 'transparent'};"></span>`
    ).join('');
  }
  _generate(seed) {
    const arr = [];
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
    for (let i = 0; i < 36; i++) {
      h = (h * 1103515245 + 12345) | 0;
      arr.push((Math.abs(h) % 3) + 1);
    }
    return arr;
  }
}
customElements.define('barcode-strip', BarcodeStrip);

/* ── TOAST ─────────────────────────────────────────────────────────────── */
function showToast(message, onAction) {
  const toast  = document.getElementById('qs-toast');
  if (!toast) return;
  toast.innerHTML = `
    <span style="font-family:var(--qs-font-mono);font-size:10px;letter-spacing:.18em;color:var(--qs-signal);">${_i18n.cart?.transmitted || 'TRANSMITTED'}</span>
    <span style="font-family:var(--qs-font-mono);font-size:11px;letter-spacing:.14em;text-transform:uppercase;">${message}</span>
    <button class="qs-toast__btn">${_i18n.cart?.view_cart || 'VIEW CART →'}</button>
  `;
  toast.classList.add('is-visible');
  toast.querySelector('.qs-toast__btn').addEventListener('click', () => {
    toast.classList.remove('is-visible');
    if (onAction) onAction();
  });
  clearTimeout(toast._timer);
  toast._timer = setTimeout(() => toast.classList.remove('is-visible'), 2800);
}

/* ── CART DRAWER ───────────────────────────────────────────────────────── */
const CartDrawer = (() => {
  const drawer  = document.getElementById('qs-cart-drawer');
  const overlay = document.getElementById('qs-cart-overlay');
  const closeBtn = document.getElementById('qs-cart-close');
  const openBtn  = document.getElementById('qs-open-cart');

  let _savedScroll = 0;
  function open()  {
    _savedScroll = window.scrollY;
    if (drawer) drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    const toast = document.getElementById('qs-toast');
    if (toast) { clearTimeout(toast._timer); toast.classList.remove('is-visible'); }
  }
  function close() { if (drawer) drawer.classList.remove('is-open'); document.body.style.overflow = ''; window.scrollTo(0, _savedScroll); }

  async function refresh() {
    try {
      const res  = await fetch('/?sections=cart-items,cart-footer');
      const data = await res.json();
      if (data['cart-items'])  document.getElementById('qs-cart-body').innerHTML  = data['cart-items'];
      if (data['cart-footer']) document.getElementById('qs-cart-footer').innerHTML = data['cart-footer'];
    } catch (e) {
      console.error('QS cart refresh failed', e);
    }
    await updateCount();
  }

  async function updateCount() {
    try {
      const res  = await fetch('/cart.js');
      const data = await res.json();
      const padded = String(data.item_count).padStart(2, '0');
      document.querySelectorAll('#qs-cart-count, #qs-cart-count-mobile').forEach(el => {
        el.textContent = padded;
      });
      const checkoutBtn = document.getElementById('qs-checkout-btn');
      if (checkoutBtn) checkoutBtn.style.opacity = data.item_count > 0 ? '' : '0.4';
    } catch (e) {}
  }

  async function addItem(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ items: [{ id: variantId, quantity }] }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Could not add item');
    }
    const data = await res.json();
    await refresh();
    return data;
  }

  async function removeItem(key) {
    await fetch('/cart/change.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ id: key, quantity: 0 }),
    });
    await refresh();
  }

  async function updateQuantity(key, quantity) {
    await fetch('/cart/change.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ id: key, quantity }),
    });
    await refresh();
  }

  if (openBtn) openBtn.addEventListener('click', open);
  if (closeBtn) closeBtn.addEventListener('click', close);
  if (drawer) drawer.addEventListener('click', (e) => {
    if (!e.target.closest('.qs-cart-drawer__panel')) close();
  });

  // Remove buttons inside cart drawer
  document.addEventListener('click', async (e) => {
    const removeBtn = e.target.closest('.qs-cart-remove');
    if (!removeBtn) return;
    e.preventDefault();
    await removeItem(removeBtn.dataset.key);
  });

  // Quick-add from product cards
  document.addEventListener('click', async (e) => {
    const addBtn = e.target.closest('.qs-quick-add');
    if (!addBtn || addBtn.disabled) return;
    e.preventDefault();
    e.stopPropagation();
    addBtn.disabled = true;
    try {
      await addItem(Number(addBtn.dataset.variantId));
      const name = addBtn.closest('[data-product-title]')?.dataset.productTitle
        || addBtn.closest('.qs-card')?.querySelector('.qs-card__name')?.textContent
        || (_i18n.products?.fallback_name || 'ARTIFACT');
      showToast(`+ ${name}`, open);
    } catch (err) {
      console.error(err);
    } finally {
      addBtn.disabled = false;
    }
  });

  return { open, close, addItem, removeItem, updateQuantity, refresh };
})();

/* ── PRODUCT FORM (PDP add to cart) ───────────────────────────────────── */
const productForm = document.getElementById('product-form');
if (productForm) {
  productForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn  = productForm.querySelector('[type="submit"]');
    const id   = Number(productForm.querySelector('[name="id"]').value);
    const qty  = Number(productForm.querySelector('[name="quantity"]')?.value || 1);
    btn.disabled = true;
    btn.querySelector('span').textContent = _i18n.products?.adding || 'ADDING...';
    try {
      await CartDrawer.addItem(id, qty);
      const title = document.querySelector('#product-form')?.closest('section')?.querySelector('h1')?.textContent || (_i18n.products?.fallback_name || 'ARTIFACT');
      showToast(`+ ${title.trim()}`, CartDrawer.open);
      CartDrawer.open();
    } catch (err) {
      console.error(err);
      btn.querySelector('span').textContent = _i18n.products?.error || 'ERROR — TRY AGAIN';
    } finally {
      setTimeout(() => {
        btn.disabled = false;
        btn.querySelector('span').textContent = btn.dataset.defaultLabel || (_i18n.products?.add_to_cart || 'ADD TO CART');
      }, 1200);
    }
  });
}

/* ── CART PAGE qty/remove ─────────────────────────────────────────────── */
document.querySelectorAll('[data-cart-qty]').forEach(input => {
  input.addEventListener('change', async () => {
    await CartDrawer.updateQuantity(input.dataset.key, Number(input.value));
    window.location.reload();
  });
});
document.querySelectorAll('[data-cart-remove]').forEach(btn => {
  btn.addEventListener('click', async () => {
    await CartDrawer.removeItem(btn.dataset.key);
    window.location.reload();
  });
});

/* ── NAVIGATION MEGA-MENU ──────────────────────────────────────────────── */
const navContainer = document.getElementById('qs-nav-container');
if (navContainer) {
  const triggers  = navContainer.querySelectorAll('[data-nav-trigger]');
  const subnavs   = navContainer.querySelectorAll('.qs-subnav');

  function closeAll() {
    subnavs.forEach(s => s.classList.remove('is-open'));
    triggers.forEach(t => t.setAttribute('aria-expanded', 'false'));
  }

  triggers.forEach(trigger => {
    trigger.addEventListener('mouseenter', () => {
      const id     = trigger.dataset.navTrigger;
      const target = document.getElementById(id);
      closeAll();
      if (target) {
        target.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      }
    });
  });

  navContainer.addEventListener('mouseleave', closeAll);

  // Keyboard accessibility
  triggers.forEach(trigger => {
    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const id      = trigger.dataset.navTrigger;
        const target  = document.getElementById(id);
        const isOpen  = trigger.getAttribute('aria-expanded') === 'true';
        closeAll();
        if (!isOpen && target) {
          target.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      }
      if (e.key === 'Escape') closeAll();
    });
  });
}

/* ── FAQ ACCORDION ─────────────────────────────────────────────────────── */
document.querySelectorAll('.qs-faq-trigger').forEach(trigger => {
  trigger.addEventListener('click', () => {
    const item   = trigger.closest('.qs-faq-item');
    const body   = item.querySelector('.qs-faq-body');
    const icon   = trigger.querySelector('.qs-faq-icon');
    const isOpen = !body.hidden;
    body.hidden  = isOpen;
    icon.textContent = isOpen ? '+' : '−';
    trigger.style.background = isOpen ? 'transparent' : 'rgba(255,90,0,0.03)';
  });
});

// FAQ category filter
const faqFilter = document.getElementById('qs-faq-filter');
if (faqFilter) {
  faqFilter.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-cat-filter]');
    if (!btn) return;
    const cat = btn.dataset.catFilter;
    faqFilter.querySelectorAll('[data-cat-filter]').forEach(b => {
      b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
    });
    document.querySelectorAll('.qs-faq-item').forEach(item => {
      item.hidden = cat !== 'TODAS' && item.dataset.cat !== cat;
    });
  });
}

// FAQ search
const faqSearch = document.getElementById('qs-faq-search');
if (faqSearch) {
  faqSearch.addEventListener('input', () => {
    const q = faqSearch.value.trim().toUpperCase();
    document.querySelectorAll('.qs-faq-item').forEach(item => {
      const text = item.textContent.toUpperCase();
      item.hidden = q.length > 0 && !text.includes(q);
    });
  });
}

/* ── SIZE CHART MODAL ──────────────────────────────────────────────────── */
const scModal = document.getElementById('qs-size-chart-modal');
if (scModal) {
  const openBtn = document.querySelector('[data-size-chart-open]');
  const closeBtns = scModal.querySelectorAll('[data-size-chart-close]');
  const tabBtns = scModal.querySelectorAll('[data-sc-tab]');
  const panels = scModal.querySelectorAll('[data-sc-panel]');

  function scOpen() {
    scModal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }
  function scClose() {
    scModal.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  if (openBtn) openBtn.addEventListener('click', scOpen);
  closeBtns.forEach(b => b.addEventListener('click', scClose));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && scModal.classList.contains('is-open')) scClose();
  });

  tabBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const cat = btn.dataset.scTab;
      tabBtns.forEach(b => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
      panels.forEach(p => { p.hidden = p.dataset.scPanel !== cat; });
    });
  });
}

/* ── SIZING TABS ───────────────────────────────────────────────────────── */
const sizingTabs = document.getElementById('qs-sizing-tabs');
if (sizingTabs) {
  sizingTabs.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-sizing-cat]');
    if (!btn) return;
    const cat = btn.dataset.sizingCat;
    sizingTabs.querySelectorAll('[data-sizing-cat]').forEach(b => {
      b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
    });
    document.querySelectorAll('[data-sizing-table]').forEach(table => {
      table.hidden = table.dataset.sizingTable !== cat;
    });
  });
}

/* ── MOBILE NAVIGATION ─────────────────────────────────────────────────── */
const hamburger   = document.getElementById('qs-hamburger');
const mobileNav   = document.getElementById('qs-mobile-nav');
const mobileClose = document.getElementById('qs-mobile-nav-close');
const mobileCart  = document.getElementById('qs-open-cart-mobile');

let _mobileNavScroll = 0;
function openMobileNav() {
  if (!mobileNav) return;
  _mobileNavScroll = window.scrollY;
  mobileNav.classList.add('is-open');
  mobileNav.setAttribute('aria-hidden', 'false');
  if (hamburger) hamburger.setAttribute('aria-expanded', 'true');
  document.body.style.overflow = 'hidden';
}
function closeMobileNav() {
  if (!mobileNav) return;
  mobileNav.classList.remove('is-open');
  mobileNav.setAttribute('aria-hidden', 'true');
  if (hamburger) hamburger.setAttribute('aria-expanded', 'false');
  document.body.style.overflow = '';
  window.scrollTo(0, _mobileNavScroll);
}

if (hamburger)   hamburger.addEventListener('click', openMobileNav);
if (mobileClose) mobileClose.addEventListener('click', closeMobileNav);
if (mobileCart)  mobileCart.addEventListener('click', () => { closeMobileNav(); CartDrawer.open(); });

// Close mobile nav when a link inside is clicked
if (mobileNav) {
  mobileNav.querySelectorAll('a[href]').forEach(link => {
    link.addEventListener('click', closeMobileNav);
  });
}

// Sync cart count in mobile nav
const CartSync = (() => {
  const desktopBadge = document.getElementById('qs-cart-count');
  const mobileBadge  = document.getElementById('qs-cart-count-mobile');
  return {
    update(count) {
      const padded = String(count).padStart(2, '0');
      if (desktopBadge) desktopBadge.textContent = padded;
      if (mobileBadge)  mobileBadge.textContent  = padded;
    }
  };
})();

/* ── MOBILE NAV ACCORDION ──────────────────────────────────────────────── */
document.querySelectorAll('[data-mobile-sub]').forEach(trigger => {
  trigger.addEventListener('click', () => {
    const panel  = document.getElementById(trigger.dataset.mobileSub);
    if (!panel) return;
    const isOpen = !panel.hidden;

    // Collapse all open sub-panels first
    document.querySelectorAll('.qs-mobile-nav__sub').forEach(sub => { sub.hidden = true; });
    document.querySelectorAll('[data-mobile-sub]').forEach(t => t.setAttribute('aria-expanded', 'false'));

    // Toggle the tapped panel
    if (!isOpen) {
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
    }
  });
});

/* ── THUMBNAIL SWITCHER (PDP) ──────────────────────────────────────────── */
function qsSwitchMainImage(src, activeBtn) {
  const mainImg = document.getElementById('qs-featured-img');
  if (!mainImg || !src) return;
  mainImg.style.opacity = '0.4';
  mainImg.onload = () => { mainImg.style.opacity = '1'; };
  mainImg.removeAttribute('srcset');
  mainImg.src = src;
  document.querySelectorAll('.qs-thumb-btn').forEach(b => {
    b.style.border = '1px solid #1A1A1A';
  });
  if (activeBtn) activeBtn.style.border = '2px solid var(--qs-signal)';
}
document.querySelectorAll('.qs-thumb-btn').forEach(btn => {
  btn.addEventListener('click', () => {
    qsSwitchMainImage(btn.dataset.src, btn);
  });
});

/* ── BUY NOW ─────────────────────────────────────────────────────────────── */
const buyNowBtn = document.getElementById('qs-buy-now');
if (buyNowBtn) {
  buyNowBtn.addEventListener('click', async () => {
    const id = Number(document.getElementById('qs-variant-id')?.value);
    if (!id || buyNowBtn.disabled) return;
    buyNowBtn.disabled = true;
    const labelEl = buyNowBtn.querySelector('span:first-child');
    if (labelEl) labelEl.textContent = _i18n.products?.adding || 'ADDING...';
    try {
      await CartDrawer.addItem(id, 1);
      window.location.href = '/checkout';
    } catch (err) {
      console.error(err);
      if (labelEl) labelEl.textContent = _i18n.products?.error || 'ERROR — TRY AGAIN';
      buyNowBtn.disabled = false;
    }
  });
}

/* ── COLLECTION SORT (AJAX — no page reload) ─────────────────────────── */
(function () {
  const sortSelect = document.getElementById('qs-collection-sort');
  const grid = document.getElementById('qs-collection-grid');
  if (!sortSelect || !grid) return;

  sortSelect.addEventListener('change', async () => {
    const url = new URL(window.location);
    url.searchParams.set('sort_by', sortSelect.value);
    history.replaceState(null, '', url);

    grid.style.opacity = '0.4';
    try {
      const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = doc.getElementById('qs-collection-grid');
      const newPag = doc.querySelector('.qs-pagination');
      if (newGrid) grid.innerHTML = newGrid.innerHTML;
      const oldPag = document.querySelector('.qs-pagination');
      if (oldPag && newPag) oldPag.innerHTML = newPag.innerHTML;
      else if (oldPag && !newPag) oldPag.remove();
    } catch (_) {
      window.location = url;
    }
    grid.style.opacity = '1';
  });
})();
